import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { wellnessThemes } from "@/data/wellnessData";
import { destinationDetails, getSlugFromName } from "@/data/destinationDetails";

const navItems = [
  { label: "Home", path: "/" },
  { label: "Wellness", path: "/wellness", hasDropdown: true },
  { label: "Destinations", path: "/destinations" },
  { label: "Slow Travel", path: "/slow-travel" },
  { label: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [wellnessOpen, setWellnessOpen] = useState(false);
  const [mobileWellnessOpen, setMobileWellnessOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Build search items
  const searchItems = [
    ...wellnessThemes.map((t) => ({ label: t.title, type: "Wellness" as const, path: `/wellness/${t.id}` })),
    ...destinationDetails.map((d) => ({ label: d.name, type: "Destination" as const, path: `/destinations/${d.slug}` })),
  ];

  const filteredItems = searchQuery.trim()
    ? searchItems.filter((item) => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setWellnessOpen(false);
    setMobileWellnessOpen(false);
  }, [location]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setWellnessOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSearchSelect = (path: string) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(path);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white shadow-sm">
      <div className="container mx-auto flex items-center justify-between py-3 px-4 lg:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="font-display text-2xl md:text-3xl tracking-wider text-foreground">
            <span className="font-light italic">Trav</span>
            <span className="font-semibold text-primary">wellness</span>
          </span>
          <span className="hidden lg:block text-[0.6rem] tracking-[0.25em] uppercase text-muted-foreground font-body border-l border-muted-foreground/30 pl-2 ml-1 leading-tight">
            Explore · Relax<br />Rejuvenate
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          {/* Search */}
          <div className="relative" ref={searchRef}>
            <div
              onClick={() => { setSearchOpen(true); }}
              className="flex items-center gap-2 w-52 lg:w-64 px-3 py-1.5 rounded-md border border-border bg-muted/40 cursor-text transition-colors hover:border-primary/40"
            >
              <Search size={14} className="text-muted-foreground shrink-0" />
              {searchOpen ? (
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground font-body"
                  placeholder="Search for Theme / Destination"
                />
              ) : (
                <span className="text-sm text-muted-foreground font-body select-none">Search for Theme / Destination</span>
              )}
            </div>
            <AnimatePresence>
              {searchOpen && searchQuery.trim() && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-border overflow-hidden z-50"
                >
                  {filteredItems.length > 0 ? (
                    <div className="max-h-60 overflow-y-auto py-1">
                      {filteredItems.map((item) => (
                        <button
                          key={item.path}
                          onClick={() => handleSearchSelect(item.path)}
                          className="w-full text-left px-4 py-2 text-sm font-body hover:bg-primary/5 hover:text-primary transition-colors flex items-center justify-between"
                        >
                          <span>{item.label}</span>
                          <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">{item.type}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="px-4 py-3 text-sm text-muted-foreground font-body">No results found</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {navItems.map((item) =>
            item.hasDropdown ? (
              <div key={item.path} className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setWellnessOpen(!wellnessOpen)}
                  className={`inline-flex items-center gap-1 font-body text-xs tracking-widest uppercase transition-colors hover:text-primary ${
                    location.pathname.startsWith("/wellness")
                      ? "text-primary font-medium"
                      : "text-muted-foreground"
                  }`}
                >
                  {item.label}
                  <ChevronDown
                    size={12}
                    className={`transition-transform duration-200 ${wellnessOpen ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {wellnessOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-lg shadow-lg border border-border overflow-hidden"
                    >
                      <Link
                        to="/wellness"
                        className="block px-4 py-3 font-body text-xs tracking-widest uppercase text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors border-b border-border"
                      >
                        All Themes
                      </Link>
                      {wellnessThemes.map((theme) => (
                        <Link
                          key={theme.id}
                          to={`/wellness/${theme.id}`}
                          className={`block px-4 py-2.5 font-body text-sm text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors ${
                            location.pathname === `/wellness/${theme.id}` ? "text-primary font-medium" : ""
                          }`}
                        >
                          {theme.title}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`font-body text-xs tracking-widest uppercase transition-colors hover:text-primary ${
                  location.pathname === item.path
                    ? "text-primary font-medium"
                    : "text-muted-foreground"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-md border-b border-border"
          >
            <div className="container mx-auto px-4 py-6 flex flex-col gap-2">
              {/* Mobile search */}
              <div className="flex items-center gap-2 px-2 py-2 mb-2 border border-border rounded-md bg-background">
                <Search size={14} className="text-muted-foreground shrink-0" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search themes or destinations…"
                  className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground font-body"
                />
              </div>
              {searchQuery.trim() && filteredItems.length > 0 && (
                <div className="mb-2 border border-border rounded-md overflow-hidden bg-background">
                  {filteredItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => { handleSearchSelect(item.path); setIsOpen(false); }}
                      className="w-full text-left px-4 py-2 text-sm font-body hover:bg-primary/5 hover:text-primary transition-colors flex items-center justify-between"
                    >
                      <span>{item.label}</span>
                      <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">{item.type}</span>
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.trim() && filteredItems.length === 0 && (
                <p className="mb-2 px-4 py-2 text-sm text-muted-foreground font-body">No results found</p>
              )}
              {navItems.map((item) =>
                item.hasDropdown ? (
                  <div key={item.path}>
                    <button
                      onClick={() => setMobileWellnessOpen(!mobileWellnessOpen)}
                      className={`w-full flex items-center justify-between font-body text-sm tracking-widest uppercase py-2 ${
                        location.pathname.startsWith("/wellness")
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                      }`}
                    >
                      {item.label}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${mobileWellnessOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileWellnessOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pl-4 flex flex-col gap-1 overflow-hidden"
                        >
                          <Link
                            to="/wellness"
                            className="font-body text-xs tracking-widest uppercase py-2 text-muted-foreground"
                          >
                            All Themes
                          </Link>
                          {wellnessThemes.map((theme) => (
                            <Link
                              key={theme.id}
                              to={`/wellness/${theme.id}`}
                              className={`font-body text-sm py-1.5 ${
                                location.pathname === `/wellness/${theme.id}`
                                  ? "text-primary font-medium"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {theme.title}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`font-body text-sm tracking-widest uppercase py-2 ${
                      location.pathname === item.path
                        ? "text-primary font-medium"
                        : "text-muted-foreground"
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
